import { computed, Ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPurchase } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { blockPurchaseIfAgeNotVerified } from "@/lib/ageVerificationPurchaseGuard";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import PaymentManager from "@/payments";
import { PurchaseDetails, PurchaseInfo, PurchaseResponse } from "@/payments/types";
import config from "@/config";
import { EVENTS } from "@/const";

const NOT_ENOUGH_FUNDS_ERROR = "{com.scrile.money.validation.SpendingBalanceValidator.message}";

interface IOneTimePurchase {
  success: boolean;
  response?: PurchaseResponse;
  purchaseDetails?: ContentPurchase;
}

export default function useOneTimePurchase(data: Ref<ContentPriceSubject>, purchaseDetails: Ref<PurchaseDetails>) {
  const { authUser } = useAuthUser();

  const purchaseByCard = async (): Promise<IOneTimePurchase> => {
    if (blockPurchaseIfAgeNotVerified(authUser.value)) {
      return { success: false };
    }
    let success = false;
    let contentPurchaseResponse: ContentPurchase | undefined = undefined;
    const response = await PaymentManager.purchase(purchaseDetails.value);
    if (response.status && response.subjectPurchased) {
      success = true;
    }
    // case for two-steps payment systems
    if (response.status && !response.subjectPurchased) {
      contentPurchaseResponse = await providers.ContentPriceProvider.purchase({ data: data.value });
      success = true;
    }
    return {
      success,
      response,
      purchaseDetails: contentPurchaseResponse,
    };
  };

  const purchaseWithBalance = async (): Promise<IOneTimePurchase> => {
    if (blockPurchaseIfAgeNotVerified(authUser.value)) {
      return { success: false };
    }
    let success = false;
    let contentPurchaseResponse: ContentPurchase | undefined = undefined;
    try {
      providers.ContentPriceProvider.setReceiveErrors(false);
      contentPurchaseResponse = await providers.ContentPriceProvider.purchase({ data: data.value });
      success = true;
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === NOT_ENOUGH_FUNDS_ERROR,
          onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
        },
      ]);
    }
    return {
      success,
      purchaseDetails: contentPurchaseResponse,
    };
  };

  const purchaseInfoDetails = computed<PurchaseInfo>(() => {
    return {
      amount: purchaseDetails.value.price ?? "",
      paymentMethod: config.featurePurchaseFromBalance ? "balance" : "card",
      purchaseType: "one_time",
    };
  });

  return {
    performOneTimePurchase: config.featurePurchaseFromBalance ? purchaseWithBalance : purchaseByCard,
    purchaseInfoDetails,
  };
}
